/**
 * Lightweight draft persistence for the KYC edit forms (GH #500). A driver who
 * switches to another app mid-entry (e.g. to read their licence) would lose the
 * typed values when the PWA page is unloaded — the forms hold state in bare
 * `useState`. We mirror the text fields to `localStorage` (synchronous, survives
 * a full PWA kill, no async load flicker) keyed by driver + doc, restore them on
 * mount, and clear on a successful save.
 *
 * TEXT fields only — the picked photo (base64, up to MBs) is intentionally NOT
 * persisted here to avoid the localStorage quota; DocPicker photos are
 * re-selectable and a background→return keeps the OS picker separate.
 */

type KycDraftKind = 'dl' | 'aadhaar' | 'pan';

const key = (driverId: string, kind: KycDraftKind) => `fleetup:kyc-draft:${driverId}:${kind}`;

export function loadKycDraft<T>(driverId: string | undefined, kind: KycDraftKind): T | null {
  if (!driverId || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key(driverId, kind));
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function saveKycDraft(driverId: string | undefined, kind: KycDraftKind, draft: unknown): void {
  if (!driverId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key(driverId, kind), JSON.stringify(draft));
  } catch {
    /* quota / private-mode — best-effort, ignore */
  }
}

export function clearKycDraft(driverId: string | undefined, kind: KycDraftKind): void {
  if (!driverId || typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(key(driverId, kind));
  } catch {
    /* ignore */
  }
}
